import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { useEventos } from "../../context/EventosContext";

import { DashboardLayout, MainContent } from "../dash/styles";
import Sidebar from "../../components/sidebar/Sidebar";
import Header from "../../components/eventos/header/Header";
import Midia from "../../components/eventos/midia/Midia";
import DetalhesEvento from "../../components/eventos/detalhesEvento/DetalhesEvento";
import ActionFooter from "../../components/actionFooter/ActionFooter";

import { Container } from "./styles";
import dayjs from "dayjs";



/* 🔹 Helper para imagem */
const getImagemUrl = (imagem) => {
  if (!imagem) return "https://placehold.co/600x200?text=Sem+Imagem";
  if (imagem.startsWith("http")) return imagem;

  const baseURL = api.defaults.baseURL;
  return `${baseURL}/${imagem.replace(/^\/?/, "")}`;
};

/* 🔹 NORMALIZA TAGS (corrige erro do Mongo) */
const normalizeTags = (tags) => {
  if (!tags) return [];

  // Caso: ['["Cultura","Educação"]']
  if (
    Array.isArray(tags) &&
    tags.length === 1 &&
    typeof tags[0] === "string" &&
    tags[0].startsWith("[")
  ) {
    try {
      return JSON.parse(tags[0]);
    } catch {
      return [];
    }
  }

  // Caso: ["Cultura", "Educação"]
  if (Array.isArray(tags)) {
    return tags.map((t) => t.trim());
  }

  // Caso: string JSON
  if (typeof tags === "string") {
    try {
      return JSON.parse(tags);
    } catch {
      return [];
    }
  }

  return [];
};

export default function EditarEventos() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setEventos } = useEventos();

  const [eventoData, setEventoData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ------------------------------------------------------------------
  // 🔹 BUSCAR EVENTO
  // ------------------------------------------------------------------
  useEffect(() => {
    const fetchEvento = async () => {
      try {
        const response = await api.get(`/api/content/eventos/${id}`);
        const ev = response.data;

        setEventoData({
          ...ev,

          // Datas
          data: ev.data ? dayjs(ev.data) : null,
          hora: ev.hora ? dayjs(ev.hora, "HH:mm") : null,

          // Tipo
          acesso: ev.acesso || "Público",
          vagas: ev.vagas || "",

          // Imagem
          imagem: ev.imagem || null,
          preview: getImagemUrl(ev.imagem),

          // 🔥 TAGS NORMALIZADAS
          tags: normalizeTags(ev.tags),

          // Outros
          contatos: ev.contatos?.length
  ? ev.contatos.map((c) => ({
      id: crypto.randomUUID(),
      telefone: c.telefone || "",
      email: c.email || "",
      redeSocial: c.redeSocial || "",
    }))
  : [
      {
        id: crypto.randomUUID(),
        telefone: "",
        email: "",
        redeSocial: "",
      },
    ],
          conteudos: ev.conteudos || [{ titulo: "", itens: [""] }],
          destacado: Boolean(ev.destacado),
          xp: ev.xp || 0,
          status: ev.status || "Upcoming",
        });
      } catch (error) {
        console.error("Erro ao buscar evento:", error);
        alert("Evento não encontrado.");
        navigate("/eventos");
      } finally {
        setLoading(false);
      }
    };

    fetchEvento();
  }, [id, navigate]);

  // ------------------------------------------------------------------
  // 🔹 SALVAR ALTERAÇÕES
  // ------------------------------------------------------------------
  const handleSave = async () => {
    if (!eventoData) return;

    const formData = new FormData();

    formData.append("titulo", eventoData.titulo);
    formData.append("descricao", eventoData.descricao);
    formData.append("organizador", eventoData.organizador);
    formData.append("local", eventoData.local);

    if (eventoData.data) {
      formData.append("data", dayjs(eventoData.data).toISOString());
    }

    if (eventoData.hora) {
      formData.append("hora", dayjs(eventoData.hora).format("HH:mm"));
    }

    formData.append("status", eventoData.status);
    formData.append("xp", String(eventoData.xp || 0));


   // ---------------- ACESSO E VAGAS ----------------
  formData.append("acesso", eventoData.acesso);

  if (eventoData.acesso === "Privado") {
    formData.append("vagas", String(eventoData.vagas || 0));
  } else {
    formData.append("vagas", "null"); // limpa quando volta para público
  }

    formData.append("tags", JSON.stringify(eventoData.tags));
    formData.append("contatos", JSON.stringify(eventoData.contatos));
    formData.append("conteudos", JSON.stringify(eventoData.conteudos));
    formData.append("destacado", String(eventoData.destacado));

    // 🔹 Só envia imagem se mudou
    if (eventoData.imagem instanceof File) {
      formData.append("imagem", eventoData.imagem);
    }

    try {
      const response = await api.put(
        `/api/content/eventos/${id}`,
        formData
      );

      // Atualiza contexto
      if (setEventos) {
        setEventos((prev) =>
          prev.map((ev) =>
            (ev._id || ev.id) === id ? response.data : ev
          )
        );
      }

      alert("Evento atualizado com sucesso!");
      navigate("/eventos");
    } catch (error) {
      console.error("Erro ao atualizar evento:", error);
      alert("Erro ao salvar alterações.");
    }
  };

  // ------------------------------------------------------------------
  // 🔹 LOADING / ERRO
  // ------------------------------------------------------------------
  if (loading) {
    return <p style={{ padding: 40 }}>Carregando evento...</p>;
  }

  if (!eventoData) {
    return <p style={{ padding: 40 }}>Evento não encontrado.</p>;
  }

  // ------------------------------------------------------------------
  // 🔹 RENDER
  // ------------------------------------------------------------------
  return (
    <DashboardLayout>
      <Sidebar />

      <Container>
        <Header
          title="Editar evento"
          description="Atualize as informações do evento"
          breadcrumbMain="Eventos"
          breadcrumbCurrent="Editar evento"
        />

        <MainContent>
          <Midia
            initialPreview={eventoData.preview}
            onFileSelect={(file, preview) =>
              setEventoData({
                ...eventoData,
                imagem: file,
                preview,
              })
            }
          />

          <DetalhesEvento
            eventoData={eventoData}
            setEventoData={setEventoData}
          />

          <ActionFooter onSave={handleSave} />
        </MainContent>
      </Container>
    </DashboardLayout>
  );
}
